// Cards (spec §13): checklist item merging and the card's vertical geometry.
// Shared so the server, templates and every client agree: a card's height is
// a pure function of how many lines each piece of text wraps to, so anything
// that knows the line counts (the client measures them; templates use text
// short enough for one line) computes exactly the same `h`.
import type { Card, ChecklistItem } from './types.ts';
import { plainText } from './rich-text.ts';

export const CARD = {
  width: 280,
  minWidth: 180,
  strip: 6,          // colour label along the top
  radius: 8,
  pad: 14,           // left/right
  top: 12,           // below the strip
  bottom: 14,
  lineHeight: 1.3,   // × font size, as client/text/layout.ts
  titleSize: 16,
  noteSize: 13,
  itemSize: 14,
  noteGap: 6,
  listGap: 12,
  itemGap: 6,
  box: 14,           // tick box
  boxGap: 8,         // tick box → item text
  itemAvatar: 18,    // per-item assignee
  itemAvatarGap: 6,
  footerGap: 12,
  footerH: 22,
  avatar: 22,
  avatarOverlap: 6,
  maxAvatars: 4,
} as const;

export const CARD_COLORS = ['#4262FF', '#30A46C', '#F76B15', '#E5484D', '#8E4EC6', '#D6A100', '#12A594', '#5F6368'];

export const CARD_LIMITS = { items: 100, assignees: 20, titleLength: 200, itemLength: 500 } as const;

/** Item ids are plain alphanumerics (never "__proto__" and friends). */
export const ITEM_ID = /^[A-Za-z0-9]{1,32}$/;
export const ITEM_KEYS = ['text', 'done', 'order', 'assignee'] as const;

/** Checklist items in display order: by `order`, ties by id. */
export function sortedItems(card: Pick<Card, 'items'>): [string, ChecklistItem][] {
  return Object.entries(card.items).sort(([ia, a], [ib, b]) =>
    a.order < b.order ? -1 : a.order > b.order ? 1 : ia < ib ? -1 : ia > ib ? 1 : 0);
}

/**
 * Applies one item op (spec §13.2): props merge into the item field by field
 * (null/undefined removes an optional field), creating it if absent, which
 * needs `text` and `order`; null removes the item. Returns `card` itself when
 * the op doesn't apply (removing or editing an item that isn't there).
 */
export function applyItemOp(card: Card, itemId: string, props: Partial<ChecklistItem> | Record<string, unknown> | null): Card {
  const cur = Object.hasOwn(card.items, itemId) ? card.items[itemId] : undefined;
  if (props === null) {
    if (!cur) return card;
    const items = { ...card.items };
    delete items[itemId];
    return { ...card, items };
  }
  const p = props as Record<string, unknown>;
  if (!cur && (typeof p.text !== 'string' || typeof p.order !== 'string')) return card;
  const merged: Record<string, unknown> = cur ? { ...cur } : { done: false };
  for (const k of ITEM_KEYS) {
    if (!(k in p)) continue;
    const v = p[k];
    if (v === null || v === undefined) delete merged[k];
    else merged[k] = v;
  }
  // Canonical key order, so equal items serialise identically on every replica.
  const item: Record<string, unknown> = {};
  for (const k of ITEM_KEYS) if (merged[k] !== undefined) item[k] = merged[k];
  return { ...card, items: { ...card.items, [itemId]: item as unknown as ChecklistItem } };
}

/** How many lines each piece of a card's text wraps to (0 note lines = no note). */
export interface CardLines { title: number; note: number; items: number[] }

export interface CardGeometry {
  titleY: number;
  noteY: number | null;
  itemYs: number[];        // top of each item row, in display order
  footerY: number | null;  // count, due date and avatars
  height: number;
}

const lh = (size: number) => size * CARD.lineHeight;

export function hasFooter(card: Pick<Card, 'items' | 'assignees' | 'due'>): boolean {
  return Object.keys(card.items).length > 0 || !!card.due || card.assignees.length > 0;
}

/** Vertical layout of a card in card-local units. */
export function cardGeometry(card: Pick<Card, 'items' | 'assignees' | 'due'>, lines: CardLines): CardGeometry {
  let y = CARD.strip + CARD.top;
  const titleY = y;
  y += Math.max(1, lines.title) * lh(CARD.titleSize);
  let noteY: number | null = null;
  if (lines.note > 0) {
    y += CARD.noteGap;
    noteY = y;
    y += lines.note * lh(CARD.noteSize);
  }
  const itemYs: number[] = [];
  if (lines.items.length) {
    y += CARD.listGap;
    lines.items.forEach((n, i) => {
      if (i) y += CARD.itemGap;
      itemYs.push(y);
      y += Math.max(Math.max(1, n) * lh(CARD.itemSize), CARD.box);
    });
  }
  let footerY: number | null = null;
  if (hasFooter(card)) {
    y += CARD.footerGap;
    footerY = y;
    y += CARD.footerH;
  }
  y += CARD.bottom;
  return { titleY, noteY, itemYs, footerY, height: Math.round(y * 100) / 100 };
}

/** Height of a card whose title and items each fit on one line and which has no note. */
export function singleLineCardHeight(card: Pick<Card, 'items' | 'assignees' | 'due'>): number {
  return cardGeometry(card, { title: 1, note: 0, items: Object.keys(card.items).map(() => 1) }).height;
}

/** Readable text for other apps (the clipboard): title, note, then the checklist. */
export function cardPlainText(card: Card): string {
  const lines = [card.title, plainText(card.note)].filter((s) => s.trim());
  for (const [, it] of sortedItems(card)) lines.push(`${it.done ? '[x]' : '[ ]'} ${it.text}`);
  return lines.join('\n');
}

export const DUE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether 'YYYY-MM-DD' is a real calendar date. */
export function isValidDue(s: string): boolean {
  if (!DUE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Today in local time as 'YYYY-MM-DD'. */
export function localToday(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Past its due date, unless its checklist is complete. */
export function isOverdue(card: Pick<Card, 'items' | 'due'>, today: string): boolean {
  if (!card.due || card.due >= today) return false;
  const items = Object.values(card.items);
  return !(items.length > 0 && items.every((i) => i.done));
}
